import { AuthenticatedApiRequest } from '@goldspotdiscoveries/auth';
import { parseQueryParams } from '../helpers/parse-query-params';
import { LogsApiDataItem, LogsApiLogObject } from '@goldspotdiscoveries/atoms';

/**
 * Fetch 1 log object by ID
 */
export const getLog = (
  company: string,
  projectId: string,
  logId: string,
): Promise<LogsApiLogObject> => {
  return new Promise((resolve, reject) => {
    AuthenticatedApiRequest({
      method: 'get',
      apiName: 'Projects',
      path: `/${company}/${projectId}/logs/${logId}`,
    })
      .then((result: any) => {
        const logObject: LogsApiLogObject = result;
        resolve(logObject);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

/**
 * Fetch a list of log objects
 *
 * Possible query parameters:
 * - limit
 * - sortBy ('asc' or 'desc', 'desc' is default)
 * - offset_token
 */
export const getLogs = (
  company: string,
  projectId: string,
  queryParams?: Record<string, string | number>,
): Promise<{ items: LogsApiLogObject[]; next_offset_token?: string }> => {
  return new Promise((resolve, reject) => {
    const validKeys: string[] = ['limit', 'sortBy', 'offset_token', 'expand'];
    const queryString: string = parseQueryParams(validKeys, queryParams);
    AuthenticatedApiRequest({
      method: 'get',
      apiName: 'Projects',
      path: `/${company}/${projectId}/logs${queryString}`,
    })
      .then((result: any) => {
        const response: {
          items: LogsApiLogObject[];
          next_offset_token?: string;
        } = result;
        resolve(response);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

/**
 * Fetch data items from a log
 *
 * Query parameters can include:
 * - properties (keys from log schema)
 * - drillhole_id (drillhole name or litholens id)
 * - limit
 * - sortBy ('asc' or 'desc', 'desc' is default)
 * - offset_token
 */
export const getLogDataItems = (
  company: string,
  projectId: string,
  logId: string,
  queryParams?: Record<string, string>,
): Promise<{ items: LogsApiDataItem[]; next_offset_token?: string }> => {
  return new Promise((resolve, reject) => {
    const validKeys: string[] = [
      'properties',
      'drillhole_id',
      'limit',
      'sortBy',
      'offset_token',
    ];
    const queryString: string = parseQueryParams(validKeys, queryParams);
    AuthenticatedApiRequest({
      method: 'get',
      apiName: 'Projects',
      path: `/${company}/${projectId}/logs/${logId}/data${queryString}`,
    })
      .then((result: any) => {
        const response: {
          items: LogsApiDataItem[];
          next_offset_token?: string;
        } = result;
        resolve(response);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

/**
 * Fetch all data items from a log, with optional filters (recursively paginates to get all data)
 *
 * Query parameters can include:
 * - properties (keys from log schema)
 * - drillhole_id (drillhole name or litholens id)
 * - limit
 * - sortBy ('asc' or 'desc', 'desc' is default)
 * - offset_token
 */
export const getAllLogDataItems = (
  company: string,
  projectId: string,
  logId: string,
  queryParams?: Record<string, string>,
): Promise<{ items: LogsApiDataItem[] }> => {
  return new Promise((resolve, reject) => {
    const items: LogsApiDataItem[] = [];
    const recursivelyGetLogDataItems = async (
      offset_token?: string,
    ): Promise<LogsApiDataItem[]> => {
      const batchQueryParams: Record<string, string> = { ...queryParams };
      delete batchQueryParams['offset_token'];
      if (offset_token) {
        batchQueryParams['offset_token'] = offset_token;
      }
      try {
        const logDataItems: {
          items: LogsApiDataItem[];
          next_offset_token?: string;
        } = await getLogDataItems(company, projectId, logId, batchQueryParams);
        if (logDataItems?.items) {
          items.push(...logDataItems.items);
        }
        if (logDataItems?.next_offset_token) {
          await recursivelyGetLogDataItems(logDataItems.next_offset_token);
        }
        return items;
      } catch (err) {
        return items;
      }
    };
    recursivelyGetLogDataItems()
      .then((items) => {
        resolve({ items });
      })
      .catch((err) => {
        reject(err);
      });
  });
};

/**
 * Create a new log object
 */
export const putLog = (
  company: string,
  project: string,
  request: any,
): Promise<LogsApiLogObject> => {
  return new Promise((resolve, reject) => {
    AuthenticatedApiRequest({
      method: 'put',
      apiName: 'Projects',
      path: `/${company}/${project}/logs`,
      postData: {
        body: request,
      },
    })
      .then((value) => {
        const logObject: LogsApiLogObject = value as any;
        resolve(logObject);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

/**
 * Add data items to a log
 */
export const putLogDataItems = (
  company: string,
  project: string,
  logId: string,
  data: Record<string, any>,
  bulk: boolean = false,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    AuthenticatedApiRequest({
      method: 'put',
      apiName: 'Projects',
      path: `/${company}/${project}/logs/${logId}/data${
        bulk ? '?bulk=true' : ''
      }`,
      postData: {
        body: data,
      },
    })
      .then((response) => {
        resolve();
      })
      .catch((err) => {
        reject(err);
      });
  });
};

/**
 * Delete log data items, optionally by drillhole name/id
 *
 * Query parameters can include:
 * - drillhole_id (drillhole name or litholens id)
 */
export const deleteLogDataItemsByDrillholes = (
  company: string,
  projectId: string,
  logId: string,
  queryParams?: Record<string, string>,
): Promise<{ message: string }> => {
  return new Promise((resolve, reject) => {
    const validKeys: string[] = ['drillhole_id'];
    const queryString: string = parseQueryParams(validKeys, queryParams);
    AuthenticatedApiRequest({
      method: 'delete',
      apiName: 'Projects',
      path: `/${company}/${projectId}/logs/${logId}/data${queryString}`,
    })
      .then((result: any) => {
        const response: { message: string } = result;
        resolve(response);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

/**
 * Delete a log object (log must have no data items)
 */
export const deleteLog = (
  company: string,
  project: string,
  logId: string,
): Promise<{ message: string }> => {
  return new Promise((resolve, reject) => {
    AuthenticatedApiRequest({
      method: 'delete',
      apiName: 'Projects',
      path: `/${company}/${project}/logs/${logId}`,
    })
      .then((result: any) => {
        const response: { message: string } = result;
        resolve(response);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

/**
 * Update a log object
 */
export const postLog = (
  company: string,
  project: string,
  logId: string,
  data: Record<string, any>,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    AuthenticatedApiRequest({
      method: 'post',
      apiName: 'Projects',
      path: `/${company}/${project}/logs/${logId}`,
      postData: {
        body: data,
      },
    })
      .then((response) => {
        resolve();
      })
      .catch((err) => {
        reject(err);
      });
  });
};
